import { useRef, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import {
  addDays,
  areIntervalsOverlapping,
  differenceInDays,
  differenceInMonths,
  getDaysInMonth,
  getOverlappingDaysInIntervals,
  isAfter,
  isEqual,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  isSameMonth,
} from "date-fns";
import { toast } from "react-hot-toast";
import { kebabCase } from "lodash";
import { Button } from "@hydra/atom/components";
import { selectActiveCompany } from "@/store/appSlice";
import { AlertModal } from "@/components/modals";
import { BoxedContent } from "@/components/common";
import { DynamicFormContainer } from "@/components/dynamic";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import { getDynamicObjectRecords } from "@/api/dynamic/dynamicObjectNameApi";
import { useCompanyAccount, useModal } from "@/hooks";
import { getFullName, formatDecimalValues } from "@/utils/helpers";
import { calculateAmountForStayedPeriod } from "@/utils/leasing/helpers";
import { defaultComponents } from "@/components/dynamic/DynamicFormContainer";
import { TableWithCheckbox } from "@/components/finance/account-receivables";
import { getRevenueRecognitionTable } from "@/components/finance/account-receivables/tableWithCheckboxData";
import showToast from "@/utils/toast/helpers";

function calculateAmountBasedOnDuration({
  annualAmount,
  fromDate,
  toDate,
  lastRecognitionDate,
  agreementStartDate,
  agreementEndDate,
  moveOutDate,
}) {
  if (!annualAmount) {
    return 0;
  }

  const contractEndDate = moveOutDate ?? agreementEndDate;

  if (isAfter(new Date(fromDate), new Date(contractEndDate))) {
    return 0;
  }

  const isFromDateFirstDayOfMonth = isFirstDayOfMonth(fromDate);
  const isToDateLastDayOfMonth = isLastDayOfMonth(toDate);
  const monthlyAmount = formatDecimalValues(Number(annualAmount / 12));
  let isFirstRecognitionMonth = false;
  let isLastRecognitionMonth = false;
  let isAlreadyRecognized = false;
  let recognizeByMonth = false;

  if (lastRecognitionDate) {
    if (
      isEqual(toDate, new Date(lastRecognitionDate)) ||
      isAfter(new Date(lastRecognitionDate), toDate)
    ) {
      isAlreadyRecognized = true;
    }
  }

  if (isAlreadyRecognized) {
    return 0;
  }

  if (
    !lastRecognitionDate &&
    isSameMonth(new Date(agreementStartDate), new Date(fromDate))
  ) {
    isFirstRecognitionMonth = true;
  }

  if (
    !differenceInMonths(
      new Date(lastRecognitionDate),
      new Date(contractEndDate)
    )
  ) {
    isLastRecognitionMonth = true;
  }

  if (isSameDay(new Date(contractEndDate), new Date(toDate))) {
    isLastRecognitionMonth = true;
  }

  const isStartDateFirstDayOfMonth = isFirstDayOfMonth(
    new Date(agreementStartDate)
  );
  const isEndDateLastDayOfMonth = isLastDayOfMonth(new Date(contractEndDate));

  if (isFirstRecognitionMonth && isStartDateFirstDayOfMonth) {
    recognizeByMonth = true;
  }

  if (isLastRecognitionMonth && isEndDateLastDayOfMonth) {
    recognizeByMonth = true;
  }

  const numberOfDays = differenceInDays(toDate, fromDate) + 1;
  const overlappingDays =
    getOverlappingDaysInIntervals(
      {
        start: new Date(agreementStartDate),
        end: new Date(contractEndDate),
      },
      {
        start: fromDate,
        end: toDate,
      }
    ) + 1;

  if (
    isFromDateFirstDayOfMonth &&
    isToDateLastDayOfMonth &&
    overlappingDays === numberOfDays
  ) {
    recognizeByMonth = true;
  }

  if (recognizeByMonth) {
    const diffInMonths = differenceInMonths(toDate, fromDate) + 1;

    const revenueAmount = Number(monthlyAmount * diffInMonths);

    return formatDecimalValues(revenueAmount);
  }

  const daysInTheMonth = getDaysInMonth(fromDate);
  const totalDaysDifference = overlappingDays / daysInTheMonth;
  const revenueAmount = Number(monthlyAmount * totalDaysDifference);

  if (!revenueAmount) {
    return 0;
  }

  return formatDecimalValues(revenueAmount);
}

const formatContracts = (
  data,
  // revenueRecognitionDetailData,
  fromDate,
  toDate,
  accounts,
  company
) => {
  const filteredData = data.filter(
    ({
      agreementStartDate,
      agreementEndDate,
      agreementEndDateWithoutGracePeriod,
      contractTerminationDate,
      moveOutDate,
    }) => {
      let terminationDate = null;
      let tenantMoveOutDate = null;

      if (moveOutDate) {
        tenantMoveOutDate = new Date(moveOutDate);

        if (isAfter(new Date(fromDate), tenantMoveOutDate)) {
          return false;
        }
      }

      if (contractTerminationDate && !moveOutDate) {
        terminationDate = new Date(contractTerminationDate);

        if (isAfter(new Date(fromDate), terminationDate)) {
          return false;
        }
      }

      try {
        const isOverlapping = areIntervalsOverlapping(
          {
            start: new Date(agreementStartDate),
            end: new Date(agreementEndDateWithoutGracePeriod ?? agreementEndDate),
          },
          {
            start: new Date(fromDate),
            end: new Date(toDate),
          },
          {
            inclusive: true,
          }
        );

        if (isOverlapping) return true;
      } catch (error) {
        return false;
      }

      return false;
    }
  );

  const items = filteredData.map((item) => {
    let terminationDate = null;
    let tenantMoveOutDate = null;
    let endDate = toDate;

    const {
      id,
      number,
      status,
      tenant,
      building,
      component,
      unit,
      contractTerminationDate,
      moveOutDate,
      agreementStartDate,
      agreementEndDate,
      agreementEndDateWithoutGracePeriod,
      annualAmount,
      contractAmount,
      utilityCharge,
      recognizedRentalRevenue,
      recognizedUtilityRevenue,
      totalCharge,
      lastRentalRevenueRecognitionDate,
      lastUtilityRevenueRecognitionDate,
    } = item;

    if (contractTerminationDate) {
      terminationDate = new Date(contractTerminationDate);
    }

    if (moveOutDate) {
      tenantMoveOutDate = new Date(moveOutDate);
    }

    if (
      terminationDate &&
      !tenantMoveOutDate &&
      isAfter(toDate, terminationDate)
    ) {
      endDate = terminationDate;
    }

    if (tenantMoveOutDate && isAfter(toDate, tenantMoveOutDate)) {
      endDate = tenantMoveOutDate;
    }

    const contractEndDate =
      agreementEndDateWithoutGracePeriod ?? agreementEndDate;

    const utilityAnnualAmount = (annualAmount / contractAmount) * utilityCharge;

    const recognizedRentalAmount = recognizedRentalRevenue ?? 0;
    const recognizedUtilityAmount = recognizedUtilityRevenue ?? 0;
    let pendingRentalAmount = contractAmount - recognizedRentalAmount;
    let pendingUtilityAmount = (utilityCharge ?? 0) - recognizedUtilityAmount;

    if (tenantMoveOutDate) {
      const contractAmountForStayedPeriod = calculateAmountForStayedPeriod({
        annualAmount,
        agreementStartDate,
        agreementEndDate,
        moveOutDate: tenantMoveOutDate,
      });
      const utilityAmountForStayedPeriod = calculateAmountForStayedPeriod({
        annualAmount: totalCharge,
        agreementStartDate,
        agreementEndDate,
        moveOutDate: tenantMoveOutDate,
      });

      pendingRentalAmount =
        contractAmountForStayedPeriod - recognizedRentalAmount;
      pendingUtilityAmount =
        utilityAmountForStayedPeriod - recognizedUtilityAmount;
    }

    if (pendingRentalAmount < 0) {
      pendingRentalAmount = 0;
    }

    if (pendingUtilityAmount < 0 || !pendingUtilityAmount) {
      pendingUtilityAmount = 0;
    }

    const pendingAmount = pendingRentalAmount + pendingUtilityAmount;

    const lastRevenueRecognitionDateForRent = lastRentalRevenueRecognitionDate;
    const lastRevenueRecognitionDateForUtility =
      lastUtilityRevenueRecognitionDate;

    // if (
    //   revenueRecognitionDetailData &&
    //   (lastRentalRevenueRecognitionDate || lastUtilityRevenueRecognitionDate)
    // ) {
    //   const isContractRecognized = revenueRecognitionDetailData.find(
    //     (rr) => rr.contract === id
    //   );

    //   if (!isContractRecognized) {
    //     if (lastRentalRevenueRecognitionDate) {
    //       if (isSameMonth(new Date(agreementStartDate), new Date(fromDate))) {
    //         lastRevenueRecognitionDateForRent = null;
    //       }

    //       lastRevenueRecognitionDateForRent = subDays(new Date(fromDate), 1);
    //     }

    //     if (lastUtilityRevenueRecognitionDate) {
    //       if (isSameMonth(new Date(agreementStartDate), new Date(fromDate))) {
    //         lastRevenueRecognitionDateForUtility = null;
    //       }

    //       lastRevenueRecognitionDateForUtility = subDays(new Date(fromDate), 1);
    //     }
    //   }
    // }

    return {
      isSelected: true,
      company: {
        label: company.name,
        value: company.id,
      },
      key: number,
      tenant: {
        label: getFullName(tenant),
        value: tenant.id,
      },
      contract: {
        label: number,
        value: id,
      },
      lastRentalRevenueRecognitionDate: lastRevenueRecognitionDateForRent,
      lastUtilityRevenueRecognitionDate: lastRevenueRecognitionDateForUtility,
      contractStatus: status,
      building: {
        label: building?.name,
        value: building?.id,
      },
      component: {
        label: component?.name,
        value: component?.id,
      },
      unit: unit.map((u) => ({
        label: u?.name,
        value: u?.id,
      })),
      agreementStartDate: new Date(agreementStartDate),
      agreementEndDate: new Date(agreementEndDate),
      rentalAmount: contractAmount,
      utilityAmount: utilityCharge,
      totalUtilityAmount: utilityCharge,
      totalAmount: contractAmount,
      moveOutDate,
      contractTerminationDate,
      totalContractAmount: contractAmount + utilityCharge,
      pendingAmount,
      recognizeRentalRevenue: true,
      recognizedRentalAmount,
      pendingRentalAmount,
      rentalRevenueRecognitionValue: calculateAmountBasedOnDuration({
        annualAmount,
        fromDate,
        toDate: endDate,
        agreementStartDate,
        agreementEndDate: contractEndDate,
        lastRecognitionDate: lastRevenueRecognitionDateForRent,
        moveOutDate: terminationDate,
      }),
      recognizedUtilityAmount,
      pendingUtilityAmount,
      recognizeUtilityRevenue: true,
      utilityRevenueRecognitionValue: calculateAmountBasedOnDuration({
        annualAmount: utilityAnnualAmount,
        fromDate,
        toDate: endDate,
        agreementStartDate,
        agreementEndDate: contractEndDate,
        lastRecognitionDate: lastRevenueRecognitionDateForUtility,
        moveOutDate: terminationDate,
      }),
      unearnedRentalRevenueAccount: accounts.unearnedRentalRevenueAccount,
      rentalRevenueAccount: accounts.rentalRevenueAccount,
      unearnedUtilityRevenueAccount: accounts.unearnedUtilityRevenueAccount,
      utilityRevenueAccount: accounts.utilityRevenueAccount,
    };
  });

  return {
    revenueRecognitionDetail: items,
  };
};

const contractItemsCalculations = (items) => {
  const filteredContracts = items.filter((i) => i.pendingAmount);

  const totalRentalAmount = filteredContracts.reduce(
    (prevValue, { rentalAmount }) => prevValue + rentalAmount,
    0
  );

  const totalRecognizedRentalAmount = filteredContracts.reduce(
    (prevValue, { recognizedRentalAmount }) =>
      prevValue + recognizedRentalAmount,
    0
  );

  const balanceRentalAmount = filteredContracts.reduce(
    (prevValue, { pendingRentalAmount }) => prevValue + pendingRentalAmount,
    0
  );

  const totalUtilityAmount = filteredContracts.reduce(
    (prevValue, { utilityAmount }) => prevValue + utilityAmount,
    0
  );

  const totalRecognizedUtilityAmount = filteredContracts.reduce(
    (prevValue, { recognizedUtilityAmount }) =>
      prevValue + recognizedUtilityAmount,
    0
  );

  const balanceUtilityAmount = filteredContracts.reduce(
    (prevValue, { pendingUtilityAmount }) => prevValue + pendingUtilityAmount,
    0
  );

  return {
    revenueRecognitionDetail: filteredContracts,
    totalRentalAmount,
    totalRecognizedRentalAmount,
    balanceRentalAmount,
    totalUtilityAmount,
    totalRecognizedUtilityAmount,
    balanceUtilityAmount,
  };
};

function HeaderRightContent({ openModal, showButton, ...rest }) {
  return (
    <defaultComponents.HeaderRightContent {...rest}>
      {showButton ? (
        <Button small bordered onClick={openModal}>
          Select contracts
        </Button>
      ) : null}
    </defaultComponents.HeaderRightContent>
  );
}

function RevenueRecognitionForm() {
  const activeCompany = useSelector(selectActiveCompany);
  const ref = useRef(null);
  const [contractTableData, setContractTableData] = useState([]);
  const [contractsMoveoutDateViewOnly, setContractsMoveoutDateViewOnly] =
    useState([]);
  const [contractsWithMissingRecognition, setContractsWithMissingRecognition] =
    useState([]);
  const [state, setState] = useState({});
  const [toastId, setToastId] = useState();
  const [accounts, setAccounts] = useState({
    unearnedRevenueAccount: null,
    revenueAccount: null,
  });
  const { isOpen, closeModal, openModal } = useModal(false);

  const defaultAccounts = useCompanyAccount({
    params: {
      includeCompanyId: true,
      isLinkedWithRecord: false,
    },
  });

  useEffect(() => {
    if (defaultAccounts) {
      const {
        unrealisedRentalRevenue,
        rentalRevenue,
        unrealisedUtilityRevenue,
        utilityRevenue,
      } = defaultAccounts;

      setAccounts((prevState) => ({
        ...prevState,
        unearnedRentalRevenueAccount: unrealisedRentalRevenue,
        rentalRevenueAccount: rentalRevenue,
        unearnedUtilityRevenueAccount: unrealisedUtilityRevenue,
        utilityRevenueAccount: utilityRevenue,
      }));
    }
  }, [defaultAccounts]);

  const { data: contractData, isLoading } = useQuery(
    [kebabCase(dynamicObjectMap.get("ContractObjectName")), "Active"],
    () =>
      getDynamicObjectRecords(dynamicObjectMap.get("ContractObjectName"), {
        sortBy: "CreatedAt",
        sortType: "DESC",
        "status[in]":
          "Active,Approved,RenewalPending,Ended,Expired,Broken,TerminationInProgress",
        selectFields:
          "Key,Number,Status,Tenant,Building,Component,Unit,ContractTerminationDate,MoveOutDate,AgreementStartDate,AgreementEndDate,AgreementEndDateWithoutGracePeriod,AnnualAmount,ContractAmount,UtilityCharge,RecognizedRentalRevenue,RecognizedUtilityRevenue,TotalCharge,LastRentalRevenueRecognitionDate,LastUtilityRevenueRecognitionDate",
      })
  );

  // const {
  //   data: revenueRecognitionData,
  //   isLoading: isLoadingRevenueRecognition,
  // } = useQuery(
  //   [
  //     kebabCase(dynamicObjectMap.get("RevenueRecognitionObjectName")),
  //     state.fromDate,
  //     state.toDate,
  //   ],
  //   () =>
  //     getDynamicObjectRecords(
  //       dynamicObjectMap.get("RevenueRecognitionObjectName"),
  //       {
  //         sortBy: "CreatedAt",
  //         sortType: "DESC",
  //         selectFields: "Key,FromDate,ToDate",
  //         status: "Posted",
  //         queryMode: "Shallow",
  //       }
  //     ),
  //   {
  //     enabled: Boolean(state.fromDate) && Boolean(state.toDate),
  //   }
  // );

  // const {
  //   data: revenueRecognitionDetailData,
  //   isLoading: isLoadingRevenueRecognitionDetail,
  // } = useQuery(
  //   [
  //     kebabCase(
  //       dynamicObjectMap.get(
  //         "RevenueRecognitionRevenueRecognitionDetailObjectName"
  //       )
  //     ),
  //     state.revenueRecognitionIds,
  //   ],
  //   () =>
  //     getDynamicObjectRecords(
  //       dynamicObjectMap.get(
  //         "RevenueRecognitionRevenueRecognitionDetailObjectName"
  //       ),
  //       {
  //         sortBy: "CreatedAt",
  //         sortType: "DESC",
  //         selectFields: "Key,Contract",
  //         "detailId[in]": state.revenueRecognitionIds.join(","),
  //         queryMode: "Shallow",
  //       }
  //     ),
  //   {
  //     enabled:
  //       Boolean(state.revenueRecognitionIds) &&
  //       Boolean(state.revenueRecognitionIds.length),
  //   }
  // );

  useEffect(() => {
    if (
      contractData ||
      isLoading
      // || revenueRecognitionDetailData ||
      // isLoadingRevenueRecognitionDetail ||
      // revenueRecognitionData ||
      // isLoadingRevenueRecognition
    ) {
      const { fromDate, toDate } = state;
      if (!fromDate || !toDate) return;
      if (
        (
          // isLoadingRevenueRecognition ||
          // isLoadingRevenueRecognitionDetail ||
          isLoading) &&
        fromDate &&
        toDate &&
        !toastId
      ) {
        setToastId(toast.loading("Loading contracts..."));
      }

      // let shouldLoadRevenueRecognitionDetail = false;

      // if (revenueRecognitionData && !state.revenueRecognitionIds) {
      //   const filteredRevenueRecognition = revenueRecognitionData?.data.filter(
      //     (rr) => {
      //       if (
      //         isSameDay(new Date(rr.fromDate), new Date(fromDate)) &&
      //         isSameDay(new Date(rr.toDate), new Date(toDate))
      //       ) {
      //         return true;
      //       }

      //       return false;
      //     }
      //   );

      //   const revenueRecognitionIds = filteredRevenueRecognition.map(
      //     (rr) => rr.id
      //   );

      //   if (revenueRecognitionIds.length) {
      //     shouldLoadRevenueRecognitionDetail = true;
      //     setState((prevState) => ({
      //       ...prevState,
      //       revenueRecognitionIds,
      //     }));
      //   }
      // }

      if (
        !contractData
        // || !revenueRecognitionData ||
        // (!revenueRecognitionDetailData && shouldLoadRevenueRecognitionDetail)
      ) {
        return;
      }

      const { revenueRecognitionDetail } = formatContracts(
        contractData.data,
        // revenueRecognitionDetailData?.data,
        fromDate,
        toDate,
        accounts,
        activeCompany
      );

      toast.dismiss(toastId);

      if (!revenueRecognitionDetail.length) {
        showToast("No contracts found for the given date range!", "info");
      } else {
        showToast("Contracts loaded!", "success");
      }

      const filteredContracts = revenueRecognitionDetail.filter((c) => {
        if (c.rentalRevenueRecognitionValue) {
          return true;
        }

        if (c.utilityRevenueRecognitionValue) {
          return true;
        }

        return false;
      });

      const contractsWithRevenueGaps = filteredContracts.filter((item) => {
        const {
          agreementStartDate,
          agreementEndDate,
          lastRentalRevenueRecognitionDate,
          lastUtilityRevenueRecognitionDate,
        } = item;

        if (new Date(fromDate) > new Date(agreementEndDate)) {
          return false;
        }

        if (!lastRentalRevenueRecognitionDate) {
          if (!isSameMonth(new Date(agreementStartDate), new Date(fromDate))) {
            return true;
          }
        }

        if (!lastUtilityRevenueRecognitionDate && item.totalUtilityAmount) {
          if (!isSameMonth(new Date(agreementStartDate), new Date(fromDate))) {
            return true;
          }
        }

        if (lastRentalRevenueRecognitionDate) {
          const nextDay = addDays(
            new Date(lastRentalRevenueRecognitionDate),
            1
          );

          if (!isSameDay(nextDay, new Date(fromDate))) {
            return true;
          }
        }

        if (lastUtilityRevenueRecognitionDate) {
          const nextDay = addDays(
            new Date(lastUtilityRevenueRecognitionDate),
            1
          );

          if (!isSameDay(nextDay, new Date(fromDate))) {
            return true;
          }
        }

        return false;
      });

      const contractsWithMissingMoveOutDate = filteredContracts.filter(
        (item) => !item.moveOutDate && item.contractTerminationDate
      );

      const contractsWithSameMonthMoveOutDate =
        contractsWithMissingMoveOutDate.filter((item) => {
          if (
            isSameMonth(
              new Date(item.contractTerminationDate),
              new Date(fromDate)
            )
          ) {
            return true;
          }

          return false;
        });

      setContractsMoveoutDateViewOnly(contractsWithSameMonthMoveOutDate);

      setContractsWithMissingRecognition(contractsWithRevenueGaps);
      const contractsWithRevenueGapIds = contractsWithRevenueGaps.map(
        (c) => c.id
      );

      const contractsWithSameMonthMoveOutDateIds =
        contractsWithSameMonthMoveOutDate.map((c) => c.id);

      const excludedIds = contractsWithRevenueGapIds.concat(
        contractsWithSameMonthMoveOutDateIds
      );

      setContractTableData(
        filteredContracts.filter((c) => !excludedIds.includes(c))
      );
      openModal();
    }

    return () => {
      if (toastId) {
        toast.dismiss(toastId);
      }
    };
  }, [
    contractData,
    // revenueRecognitionDetailData,
    // revenueRecognitionData,
    state,
    isLoading,
    // isLoadingRevenueRecognition,
    // isLoadingRevenueRecognitionDetail,
  ]);

  const setRevenueRecognitionDetail = (key, value) => {
    const formState = ref.current.getState();
    formState[key] = value;
    const { fromDate, toDate } = formState;

    if (!fromDate || !toDate) return;

    const { fromDate: existingFromDate, toDate: existingToDate } = state;

    if (
      !isSameDay(fromDate, existingFromDate) ||
      !isSameDay(toDate, existingToDate)
    ) {
      ref?.current.setFormState({
        revenueRecognitionIds: null,
        revenueRecognitionDetail: [],
        totalOpenRentalAmount: 0,
        totalRentalAmount: 0,
        totalOpenUtilityAmount: 0,
        totalUtilityAmount: 0,
        totalOpenContractAmount: 0,
        totalContractAmount: 0,
      });
    }

    setState({ fromDate, toDate });
  };

  const setTotalContractAmount = (key, value) => {
    const formState = ref.current.getState();
    formState[key] = value;
    const { revenueRecognitionDetail: contracts } = formState;

    const totalRentalAmount = contracts.reduce(
      (prevValue, { rentalAmount }) => prevValue + rentalAmount,
      0
    );

    const totalOpenRentalAmount = contracts.reduce(
      (prevValue, { pendingRentalAmount }) => prevValue + pendingRentalAmount,
      0
    );

    const totalUtilityAmount = contracts.reduce(
      (prevValue, { utilityAmount }) => prevValue + utilityAmount,
      0
    );

    const totalOpenUtilityAmount = contracts.reduce(
      (prevValue, { pendingUtilityAmount }) => prevValue + pendingUtilityAmount,
      0
    );

    const totalContractAmount = contracts.reduce(
      (prevValue, { totalAmount }) => prevValue + totalAmount,
      0
    );

    const totalOpenContractAmount = contracts.reduce(
      (prevValue, { pendingAmount }) => prevValue + pendingAmount,
      0
    );

    ref.current.setFormState({
      totalOpenRentalAmount,
      totalRentalAmount,
      totalOpenUtilityAmount,
      totalUtilityAmount,
      totalOpenContractAmount,
      totalContractAmount,
    });
  };

  const onStateChange = (key, value) => {
    switch (key) {
      case "fromDate":
      case "toDate": {
        setRevenueRecognitionDetail(key, value);
        break;
      }

      case "revenueRecognitionDetail":
        setTotalContractAmount(key, value);
        break;

      default:
        break;
    }
  };

  const handleConfirm = () => {
    if (contractTableData.length) {
      const selectedContracts = contractTableData.filter((q) => q.isSelected);

      if (selectedContracts) {
        const {
          totalRentalAmount,
          totalOpenRentalAmount,
          totalUtilityAmount,
          totalOpenUtilityAmount,
          totalOpenContractAmount,
          totalContractAmount,
        } = contractItemsCalculations(selectedContracts);

        ref.current.setFormState({
          revenueRecognitionDetail: selectedContracts,
          totalRentalAmount,
          totalOpenRentalAmount,
          totalUtilityAmount,
          totalOpenUtilityAmount,
          totalContractAmount,
          totalOpenContractAmount,
        });
      }
    }

    closeModal();
  };

  return (
    <BoxedContent>
      <AlertModal
        icon="file-check-stroke-icon"
        iconClass="success"
        title="Select Contracts"
        subtitle="Selected revenue recognition has following active contracts"
        onClose={closeModal}
        isOpen={isOpen}
        onConfirm={handleConfirm}
        size="large"
      >
        {contractsWithMissingRecognition.length ? (
          <>
            <div className="table-header-text">
              Contracts with missing revenue recognition:
            </div>
            <TableWithCheckbox
              type="without-checkbox"
              data={contractsWithMissingRecognition}
              searchKey={["contract.label"]}
              columns={getRevenueRecognitionTable()}
              setData={setContractTableData}
            />
          </>
        ) : null}

        {contractsMoveoutDateViewOnly.length ? (
          <>
            <div className="table-header-text">
              Contracts without move out date:
            </div>
            <TableWithCheckbox
              type="without-checkbox"
              data={contractsMoveoutDateViewOnly}
              searchKey={["contract.label"]}
              columns={getRevenueRecognitionTable()}
              setData={setContractTableData}
            />
          </>
        ) : null}

        {contractTableData.length ? (
          <>
            <div className="table-header-text mt-20">
              Contracts that can be recognized:
            </div>
            <TableWithCheckbox
              data={contractTableData}
              searchKey={["contract.label"]}
              columns={getRevenueRecognitionTable()}
              setData={setContractTableData}
              selectAll
              allowMultiple
            />
          </>
        ) : null}
      </AlertModal>
      <DynamicFormContainer
        ref={ref}
        objectName={dynamicObjectMap.get("RevenueRecognitionObjectName")}
        showHeader
        components={{
          HeaderRightContent: (props) =>
            HeaderRightContent({
              openModal,
              showButton: Boolean(contractTableData.length),
              ...props,
            }),
        }}
        onStateChange={onStateChange}
      />
    </BoxedContent>
  );
}

export default RevenueRecognitionForm;
